import ThreadsHeader from "@/modules/assistants/threads/components/threads-header";
import ThreadsList, {
  ThreadsListHeader,
  ThreadsListItem,
  ThreadsListTitle,
} from "@/modules/assistants/threads/components/threads-list/threads-list";
import Threads, {
  ThreadsContent,
  ThreadsGroup,
} from "@/modules/assistants/threads/threads";
import {
  formatThreadDayLabel,
  groupThreadsByDay,
} from "@/modules/assistants/threads/threads.utils";
import { config } from "@/lib/config";
import { useSidebar } from "@/lib/components/sidebar/sidebar.provider";
import { FullPageSpinner } from "@/lib/components/ui/spinner";
import useIdle from "@/lib/hooks/use-idle";
import { WebSocketProvider as WebSocketProviderCore } from "@soulbyte/core";
import { useAuth } from "@frontegg/react";
import type { ReactNode } from "react";
import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useSearchParams } from "react-router";
import { useEvalSimulationEnabled } from "./simulate/use-eval-simulation";
import SimulatePanel from "./simulate/simulate-panel";
import { SimulateProvider, useSimulate } from "./simulate/simulate.store";

function WebSocketProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const idle = useIdle(5 * 60 * 1000); // 5 minutes
  const { run } = useSimulate();
  // A simulation is the one case where the tab is working hard with nobody
  // at the keyboard. Left to the idle gate it kills its own socket after five
  // minutes and every remaining question goes nowhere, so a live run counts
  // as activity.
  const isSimulating = run != null && run.phase !== "done";

  return (
    <WebSocketProviderCore
      idle={idle && !isSimulating}
      baseUrl={import.meta.env.VITE_WS_URL}
      accessToken={user?.accessToken}
      maxDisconnections={config.chat.websocket.maxDisconnections}
    >
      {children}
    </WebSocketProviderCore>
  );
}

export default function ChatLayout() {
  const { setSubmenu } = useSidebar();
  const [searchParams] = useSearchParams();
  const assistantId = searchParams.get("assistantId") ?? undefined;
  const showSimulate = useEvalSimulationEnabled();

  // Set the submenu when the component mounts and clear it when it unmounts.
  // Without a module there is no history, so the panel stays away entirely.
  useEffect(() => {
    setSubmenu(
      assistantId ? <RenderThreads assistantId={assistantId} /> : null,
    );

    return () => {
      setSubmenu(null);
    };
  }, [setSubmenu, searchParams, assistantId]);

  return (
    <div
      className="relative isolate flex max-h-svh min-h-svh flex-col bg-background"
      data-slot="chat-layout"
    >
      <SimulateProvider>
        <WebSocketProvider>
          <Suspense fallback={<FullPageSpinner />}>
            <Outlet />
          </Suspense>
        </WebSocketProvider>
        {showSimulate && <SimulatePanel />}
      </SimulateProvider>
    </div>
  );
}

function RenderThreads({ assistantId }: { assistantId: string }) {
  const { t, i18n } = useTranslation();

  return (
    <Threads assistantId={assistantId}>
      <ThreadsHeader />
      <ThreadsContent>
        {({ threads, pinnedThreads }) => (
          <>
            {pinnedThreads.length > 0 && (
              <ThreadsGroup>
                <ThreadsListHeader>
                  <ThreadsListTitle>{t("threads_pinned")}</ThreadsListTitle>
                </ThreadsListHeader>
                <ThreadsList>
                  {pinnedThreads.map((thread) => (
                    <ThreadsListItem key={thread.id} thread={thread} />
                  ))}
                </ThreadsList>
              </ThreadsGroup>
            )}
            {groupThreadsByDay(threads).map((group) => (
              <ThreadsGroup key={group.key}>
                <ThreadsListHeader>
                  <ThreadsListTitle>
                    {formatThreadDayLabel(group.key, i18n.language, {
                      today: t("threads_group_today"),
                      yesterday: t("threads_group_yesterday"),
                    })}
                  </ThreadsListTitle>
                </ThreadsListHeader>
                <ThreadsList>
                  {group.threads.map((thread) => (
                    <ThreadsListItem key={thread.id} thread={thread} />
                  ))}
                </ThreadsList>
              </ThreadsGroup>
            ))}
          </>
        )}
      </ThreadsContent>
    </Threads>
  );
}
